import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { apiClient } from '../services/apiClient';
import { useWorkspaces } from '../providers/WorkspaceProvider';
import { Button } from '../components/Button';
import { Input, InputWrapper, Label, ErrorMessage } from '../components/Input';
import { Modal, ModalActions } from '../components/Modal';
import { PageSkeleton } from '../components/PageSkeleton';
import type { WorkspaceMember, WorkspaceMemberRole } from '@agency-smm/shared';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xxl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const MemberTable = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
`;

const MemberRow = styled.div`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  gap: ${({ theme }) => theme.spacing.md};

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-wrap: wrap;
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  }
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: linear-gradient(135deg, #3B82F6, #8B5CF6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  font-size: ${({ theme }) => theme.fontSize.sm};
  flex-shrink: 0;
`;

const MemberInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const MemberName = styled.div`
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const MemberEmail = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textMuted};
`;

const RoleBadge = styled.span<{ $role: WorkspaceMemberRole }>`
  display: inline-block;
  padding: 2px 10px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  background: ${({ $role }) => {
    switch ($role) {
      case 'owner': return '#8B5CF620';
      case 'admin': return '#3B82F620';
      case 'editor': return '#10B98120';
      case 'viewer': return '#6B728020';
      default: return '#6B728020';
    }
  }};
  color: ${({ $role }) => {
    switch ($role) {
      case 'owner': return '#8B5CF6';
      case 'admin': return '#3B82F6';
      case 'editor': return '#10B981';
      case 'viewer': return '#6B7280';
      default: return '#6B7280';
    }
  }};
`;

const RoleSelect = styled.select`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: pointer;
`;

const MemberActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: center;
`;

const InviteForm = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  align-items: flex-end;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xxl};
  color: ${({ theme }) => theme.colors.textMuted};
`;

// Banner informativo (cupo de colaboradores + roles). Theme-aware.
const InfoBanner = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ theme }) => theme.colors.surfaceHover};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 8px 12px;
  margin-bottom: ${({ theme }) => theme.spacing.md};

  strong { color: ${({ theme }) => theme.colors.text}; }
`;

// Distintivo de alcance del colaborador (todos / este workspace).
const ScopeBadgeAll = styled.span`
  display: inline-block;
  font-size: 11px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.secondary};
  background: ${({ theme }) => theme.colors.secondary}1f;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  padding: 1px 8px;
`;

const ScopeBadgeThis = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

// Dropdown de resultados de búsqueda de usuarios.
const SearchResults = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-top: 4px;
  max-height: 180px;
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.surface};
`;

const SearchResultButton = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.text};

  &:last-child { border-bottom: none; }
  &:hover { background: ${({ theme }) => theme.colors.surfaceHover}; }

  .name { font-weight: ${({ theme }) => theme.fontWeight.semibold}; font-size: 14px; }
  .email { font-size: 12px; color: ${({ theme }) => theme.colors.textSecondary}; }
`;

const Hint = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: 4px;

  strong { color: ${({ theme }) => theme.colors.textSecondary}; }
`;

// Selector de alcance (este / elegir / todos). Pills theme-aware:
// el problema en dark era texto claro sobre fondo blanco fijo (invisible).
const ScopeRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 4px;
`;

const ScopeButton = styled.button<{ $active: boolean }>`
  padding: 7px 12px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  border: ${({ $active, theme }) =>
    $active ? `2px solid ${theme.colors.primary}` : `1px solid ${theme.colors.border}`};
  background: ${({ $active, theme }) => ($active ? `${theme.colors.primary}1a` : theme.colors.surface)};
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.text)};

  &:hover { border-color: ${({ theme }) => theme.colors.primary}; }
`;

const WsSelectList = styled.div`
  margin-top: 8px;
  max-height: 180px;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 6px;
  background: ${({ theme }) => theme.colors.surface};
`;

const WsSelectItem = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 6px;
  cursor: pointer;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
  border-radius: ${({ theme }) => theme.borderRadius.sm};

  &:hover { background: ${({ theme }) => theme.colors.surfaceHover}; }
`;

function getInitials(name?: string, email?: string): string {
  if (name) {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  }
  return (email || '?')[0].toUpperCase();
}

export function TeamPage() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { workspaces } = useWorkspaces();
  // Workspaces propios (donde el usuario puede asignar colaboradores).
  const ownedWorkspaces = workspaces.filter((w) => w.owned !== false);
  const [members, setMembers] = useState<(WorkspaceMember & { scope?: string })[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Invite modal
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<WorkspaceMemberRole>('editor');
  const [inviteError, setInviteError] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  // Alcance: este workspace / elegir varios / todos (presentes y futuros).
  const [inviteScope, setInviteScope] = useState<'workspace' | 'select' | 'all'>('workspace');
  const [selectedWsIds, setSelectedWsIds] = useState<Set<string>>(new Set());
  // Búsqueda de usuarios registrados (para invitar sin saber el email exacto).
  const [userSearch, setUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState<{ id: string; name: string; email: string }[]>([]);

  useEffect(() => {
    const q = userSearch.trim();
    if (q.length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const data = await apiClient.get<{ users: { id: string; name: string; email: string }[] }>(
          `/api/users/search?q=${encodeURIComponent(q)}`,
        );
        setSearchResults(data.users || []);
      } catch { setSearchResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [userSearch]);

  // Remove confirm
  const [removingMember, setRemovingMember] = useState<WorkspaceMember | null>(null);

  const fetchMembers = useCallback(async () => {
    if (!workspaceId) return;
    setIsLoading(true);
    try {
      const data = await apiClient.get<(WorkspaceMember & { scope?: string })[]>(
        `/api/workspaces/${workspaceId}/members`
      );
      setMembers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load team');
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleInvite = async () => {
    if (!workspaceId || !inviteEmail) return;
    if (inviteScope === 'select' && selectedWsIds.size === 0) {
      setInviteError('Pick at least one workspace.');
      return;
    }
    setInviteError('');
    setInviteLoading(true);

    const payload: { email: string; role: string; scope: 'workspace' | 'all'; workspaceIds?: string[] } = {
      email: inviteEmail,
      role: inviteRole,
      scope: inviteScope === 'all' ? 'all' : 'workspace',
    };
    if (inviteScope === 'select') payload.workspaceIds = Array.from(selectedWsIds);

    try {
      await apiClient.post(`/api/workspaces/${workspaceId}/members`, payload);
      setShowInvite(false);
      setInviteEmail('');
      setInviteRole('editor');
      setInviteScope('workspace');
      setSelectedWsIds(new Set());
      fetchMembers();
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Failed to invite member');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRoleChange = async (memberId: string, newRole: string) => {
    if (!workspaceId) return;
    try {
      await apiClient.put(`/api/workspaces/${workspaceId}/members/${memberId}`, {
        role: newRole,
      });
      fetchMembers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role');
    }
  };

  const handleRemove = async () => {
    if (!workspaceId || !removingMember) return;
    try {
      await apiClient.delete(`/api/workspaces/${workspaceId}/members/${removingMember.id}`);
      setRemovingMember(null);
      fetchMembers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove member');
    }
  };

  if (isLoading) {
    return <PageSkeleton />;
  }

  return (
    <>
      <PageHeader>
        <div>
          <Title>Collaborators</Title>
          <Subtitle>{members.length} {members.length !== 1 ? 'people' : 'person'} with access to this workspace</Subtitle>
        </div>
        <Button onClick={() => setShowInvite(true)}>+ Invite collaborator</Button>
      </PageHeader>

      <InfoBanner>
        Your subscription includes up to <strong>5 free collaborators</strong> (they don't pay extra). Roles:
        <strong> viewer</strong> = read only · <strong>editor</strong> = generate/edit · <strong>admin</strong> =
        also invite. Only the owner can delete the workspace.
      </InfoBanner>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <MemberTable>
        {members.length === 0 ? (
          <EmptyState>No collaborators yet. Invite someone by their email to share this workspace.</EmptyState>
        ) : (
          members.map(member => (
            <MemberRow key={member.id}>
              <Avatar>{getInitials(member.userName, member.userEmail)}</Avatar>
              <MemberInfo>
                <MemberName>{member.userName || 'Unknown'}</MemberName>
                <MemberEmail>{member.userEmail || ''}</MemberEmail>
                {member.scope === 'all' && (
                  <ScopeBadgeAll>All workspaces</ScopeBadgeAll>
                )}
                {member.scope === 'workspace' && (
                  <ScopeBadgeThis>This workspace</ScopeBadgeThis>
                )}
              </MemberInfo>
              {member.role === 'owner' ? (
                <RoleBadge $role="owner">Owner</RoleBadge>
              ) : (
                <MemberActions>
                  <RoleSelect
                    value={member.role}
                    onChange={(e) => handleRoleChange(member.id, e.target.value)}
                  >
                    <option value="admin">Admin</option>
                    <option value="editor">Editor</option>
                    <option value="viewer">Viewer</option>
                  </RoleSelect>
                  <Button
                    $variant="outline"
                    $size="sm"
                    onClick={() => setRemovingMember(member)}
                    style={{ color: '#EF4444', borderColor: '#EF4444' }}
                  >
                    Remove
                  </Button>
                </MemberActions>
              )}
            </MemberRow>
          ))
        )}
      </MemberTable>

      {/* Invite Modal */}
      <Modal
        isOpen={showInvite}
        onClose={() => { setShowInvite(false); setInviteError(''); setUserSearch(''); setSearchResults([]); setInviteScope('workspace'); setSelectedWsIds(new Set()); }}
        title="Invite collaborator"
      >
        <div style={{ marginBottom: 12 }}>
          <Label>Search registered user</Label>
          <Input
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            placeholder="Name or email…"
            autoFocus
          />
          {searchResults.length > 0 && (
            <SearchResults>
              {searchResults.map((u) => (
                <SearchResultButton
                  key={u.id}
                  type="button"
                  onClick={() => { setInviteEmail(u.email); setUserSearch(''); setSearchResults([]); }}
                >
                  <div className="name">{u.name}</div>
                  <div className="email">{u.email}</div>
                </SearchResultButton>
              ))}
            </SearchResults>
          )}
          {userSearch.trim().length >= 2 && searchResults.length === 0 && (
            <Hint>No results — try another name or use the exact email.</Hint>
          )}
        </div>

        <InviteForm>
          <InputWrapper style={{ flex: 1 }}>
            <Label>Collaborator email</Label>
            <Input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="auto-filled on select, or type it"
            />
          </InputWrapper>
          <InputWrapper>
            <Label>Role</Label>
            <RoleSelect
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as WorkspaceMemberRole)}
              style={{ width: '120px', height: '40px' }}
            >
              <option value="admin">Admin</option>
              <option value="editor">Editor</option>
              <option value="viewer">Viewer</option>
            </RoleSelect>
          </InputWrapper>
        </InviteForm>

        <div style={{ marginTop: 14 }}>
          <Label>Workspace access</Label>
          <ScopeRow>
            {([['workspace', 'This workspace'], ['select', 'Choose workspaces'], ['all', 'All (+ future)']] as const).map(([s, label]) => (
              <ScopeButton
                key={s}
                type="button"
                $active={inviteScope === s}
                onClick={() => setInviteScope(s)}
              >{label}</ScopeButton>
            ))}
          </ScopeRow>
          {inviteScope === 'all' && (
            <Hint style={{ marginTop: 6 }}>
              Full account access: every workspace you own now <strong>and any you create later</strong>.
            </Hint>
          )}
          {inviteScope === 'select' && (
            <WsSelectList>
              {ownedWorkspaces.length === 0 ? (
                <Hint style={{ padding: 4, marginTop: 0 }}>You don't own any workspaces.</Hint>
              ) : ownedWorkspaces.map((w) => (
                <WsSelectItem key={w.id}>
                  <input
                    type="checkbox"
                    checked={selectedWsIds.has(w.id)}
                    onChange={(e) => setSelectedWsIds((prev) => {
                      const next = new Set(prev);
                      if (e.target.checked) next.add(w.id); else next.delete(w.id);
                      return next;
                    })}
                  />
                  {w.name}
                </WsSelectItem>
              ))}
            </WsSelectList>
          )}
        </div>

        {inviteError && <ErrorMessage style={{ marginTop: '8px' }}>{inviteError}</ErrorMessage>}
        <ModalActions>
          <Button $variant="outline" onClick={() => setShowInvite(false)}>Cancel</Button>
          <Button onClick={handleInvite} disabled={inviteLoading || !inviteEmail}>
            {inviteLoading ? 'Inviting...' : 'Send Invite'}
          </Button>
        </ModalActions>
      </Modal>

      {/* Remove Confirmation Modal */}
      <Modal
        isOpen={!!removingMember}
        onClose={() => setRemovingMember(null)}
        title="Remove Team Member"
        titleVariant="error"
        message={`Are you sure you want to remove ${removingMember?.userName || removingMember?.userEmail} from this workspace?`}
      >
        <ModalActions>
          <Button $variant="outline" onClick={() => setRemovingMember(null)}>Cancel</Button>
          <Button
            onClick={handleRemove}
            style={{ background: '#EF4444' }}
          >
            Remove
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
}
